import { medida } from '@/api/formaleta';

// El croquis en vivo mientras se escriben las medidas.
//
// Regla dura del expediente, y no es un adorno: ya existe en el `+ Nuevo` de la
// canvas app (un rectángulo 2D proporcional que se redibuja al teclear) y es lo
// único que le dice al operario que acertó las medidas ANTES de guardar. Daniel
// lo pidió explícitamente conservar: «es el patrón correcto».
//
// Se dibuja con SVG a mano y no con una librería: son dos rectángulos y cuatro
// textos, y meter una dependencia de gráficos para esto sería pagar un bundle
// por nada.

const ANCHO_CAJA = 230;
const ALTO_CAJA = 150;
const MARGEN = 26;

export function Croquis({ ancho, largo, fondo, color }: {
  /** cm */
  ancho: number | null;
  /** cm */
  largo: number | null;
  /** cm. Se insinúa como profundidad, no se dibuja en 3D. */
  fondo: number | null;
  /** El color de la categoría elegida. Viene de la base. */
  color?: string | null;
}) {
  const a = ancho && ancho > 0 ? ancho : null;
  const l = largo && largo > 0 ? largo : null;
  const trazo = color || '#747B86';

  // Sin ninguna medida no hay nada que dibujar: se muestra el marco punteado
  // para que el hueco se lea como «escribí las medidas» y no como un error.
  if (!a && !l) {
    return (
      <svg width={ANCHO_CAJA} height={ALTO_CAJA} className="shrink-0" role="img"
        aria-label="Croquis vacío: faltan las medidas">
        <rect
          x={MARGEN} y={MARGEN}
          width={ANCHO_CAJA - MARGEN * 2} height={ALTO_CAJA - MARGEN * 2}
          fill="none" stroke="#D9D9D9" strokeWidth={1.5} strokeDasharray="5 4"
        />
        <text
          x={ANCHO_CAJA / 2} y={ALTO_CAJA / 2 + 4}
          textAnchor="middle" fontSize={11} fill="#AAAFB6"
        >
          escribí las medidas
        </text>
      </svg>
    );
  }

  // Con una sola medida se asume cuadrado para poder dibujar algo proporcional
  // en vez de un rectángulo degenerado de un pixel.
  const cmAncho = a ?? l!;
  const cmLargo = l ?? a!;

  const dispX = ANCHO_CAJA - MARGEN * 2;
  const dispY = ALTO_CAJA - MARGEN * 2;
  // Una sola escala para los dos ejes: si cada eje llevara la suya, un panel de
  // 20×240 se vería cuadrado y el croquis mentiría justo en lo que sirve.
  const escala = Math.min(dispX / cmAncho, dispY / cmLargo);
  const w = Math.max(6, cmAncho * escala);
  const h = Math.max(6, cmLargo * escala);
  const x = (ANCHO_CAJA - w) / 2;
  const y = (ALTO_CAJA - h) / 2;

  // La profundidad se insinúa con un desplazamiento tope de 10 px: es una
  // pista visual de que la pieza tiene fondo, no una proyección isométrica.
  const d = fondo && fondo > 0 ? Math.min(10, Math.max(3, fondo * escala)) : 0;

  return (
    <svg width={ANCHO_CAJA} height={ALTO_CAJA} className="shrink-0" role="img"
      aria-label={`Croquis de ${medida(cmAncho)} por ${medida(cmLargo)} centímetros`}>
      {d > 0 && (
        <>
          <path
            d={`M ${x} ${y} l ${d} ${-d} l ${w} 0 l ${-d} ${d} Z`}
            fill={trazo} opacity={0.25}
          />
          <path
            d={`M ${x + w} ${y} l ${d} ${-d} l 0 ${h} l ${-d} ${d} Z`}
            fill={trazo} opacity={0.4}
          />
        </>
      )}
      <rect x={x} y={y} width={w} height={h} fill={trazo} fillOpacity={0.12}
        stroke={trazo} strokeWidth={2} />

      {/* Cota horizontal = ancho */}
      <line x1={x} y1={y + h + 10} x2={x + w} y2={y + h + 10}
        stroke="#747B86" strokeWidth={1} />
      <text x={x + w / 2} y={y + h + 23} textAnchor="middle" fontSize={11}
        fontWeight={600} fill="#5D636C">
        {a ? `${medida(a)} cm` : '?'}
      </text>

      {/* Cota vertical = largo */}
      <line x1={x - 10} y1={y} x2={x - 10} y2={y + h}
        stroke="#747B86" strokeWidth={1} />
      <text x={x - 14} y={y + h / 2 + 4} textAnchor="end" fontSize={11}
        fontWeight={600} fill="#5D636C">
        {l ? `${medida(l)}` : '?'}
      </text>

      {fondo && fondo > 0 && (
        <text x={x + w + d + 4} y={y - d + 10} fontSize={10} fill="#747B86">
          f {medida(fondo)}
        </text>
      )}
    </svg>
  );
}
